import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useVirtualizer } from '@tanstack/react-virtual';
import { format, isSameYear, isToday } from 'date-fns';
import {
  AlertTriangle,
  Archive,
  CheckSquare2,
  Clock3,
  ChevronDown,
  ChevronRight,
  Inbox,
  Loader2,
  Mail,
  MailOpen,
  Paperclip,
  RefreshCw,
  Search as SearchIcon,
  Square,
  Star,
  Trash2,
} from 'lucide-react';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from 'react';
import {
  broadenMailSearchQuery,
  hasPrefixMailSearchTerms,
  mailSearchTextTerms,
  MAX_SEARCH_BULK_THREADS,
  narrowMailSearchQuery,
  parseMailSearchSyntax,
  type Account,
  type MailColumn,
  type MailColumnWidths,
  type MailSearchMatch,
  type MailSearchSort,
  type MailSearchTextTerm,
  type MessageActionRequest,
  type SpecialUse,
  type ThreadRow,
} from '@email/shared';
import { toast } from 'sonner';
import {
  useAccounts,
  useFolders,
  useMailNavigationCounts,
} from '../../api/queries.js';
import {
  prefetchThreadOpen,
  remoteImageRequestMode,
  useSearchResults,
  useSnoozeThreads,
  useEmptyTrash,
  useThreadList,
  useUnreadList,
  useUnsnoozeThreads,
  type EmptyTrashProgress,
  type MailScope,
} from '../../api/mail.js';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts.js';
import { useMediaQuery } from '../../hooks/useMediaQuery.js';
import {
  useUiStore,
  type AccountIdentityMode,
  type MailListDensity,
} from '../../stores/ui.js';
import { useMailActionRunner } from '../mail-actions.js';
import { beginMailDrag, endMailDrag } from '../mail-drag.js';
import { useMailPaneKeyboard } from '../mail-view/MailWorkspace.js';
import { PermanentDeleteDialog } from '../permanent-delete/PermanentDeleteDialog.js';
import { EmptyTrashDialog } from '../permanent-delete/EmptyTrashDialog.js';
import { MessageContextMenu } from './MessageContextMenu.js';
import { MessageToolbar } from './MessageToolbar.js';
import { resolveSenderSubjectLayout } from './row-layout.js';
import { SnoozeDialog } from '../snooze/SnoozeDialog.js';

const SavedSearchesDialog = lazy(() =>
  import('../saved-searches/SavedSearchesDialog.js').then((module) => ({
    default: module.SavedSearchesDialog,
  })),
);

type DisplayThreadRow = ThreadRow & { match?: MailSearchMatch | null };

type ListItem =
  | { kind: 'header'; label: string; count: number; section: 'unread' | 'rest' }
  | { kind: 'thread'; row: DisplayThreadRow }
  | { kind: 'loader' };

const ROW_HEIGHTS = { compact: 34, comfortable: 40, spacious: 50 } as const;
const STACKED_ROW_HEIGHTS = { compact: 52, comfortable: 60, spacious: 68 } as const;
const HEADER_HEIGHTS = { compact: 30, comfortable: 34, spacious: 38 } as const;
const MAX_BULK_THREADS = MAX_SEARCH_BULK_THREADS;

interface ContextMenuState {
  x: number;
  y: number;
  rowId: number;
  targetIds: number[];
}

interface PermanentDeleteRequest {
  targetIds: number[];
}

export function formatRowDate(ms: number): string {
  const d = new Date(ms);
  if (isToday(d)) return format(d, 'h:mm a');
  if (isSameYear(d, new Date())) return format(d, 'MMM d');
  return format(d, 'M/d/yy');
}

function participantsLabel(row: ThreadRow): string {
  const names = row.participants.map((p) => p.name.trim() || p.email.split('@')[0]!);
  const label = names.slice(0, 3).join(', ');
  return row.messageCount > 1 ? `${label} (${row.messageCount})` : label || '(unknown)';
}

type MessageListProps = (
  | { scope: MailScope; searchQuery?: never }
  | { scope?: never; searchQuery: string; searchSort: MailSearchSort }
) & { previewThreadId?: number };

export function MessageList(props: MessageListProps) {
  const queryClient = useQueryClient();
  const { data: folders } = useFolders();
  const { data: accounts } = useAccounts();
  const navigate = useNavigate();
  const action = useMailActionRunner();
  const snooze = useSnoozeThreads();
  const unsnooze = useUnsnoozeThreads();
  const emptyTrash = useEmptyTrash();
  const keyboardEnabled = useMailPaneKeyboard('list');
  const accountIdentityMode = useUiStore((state) => state.accountIdentityMode);
  const density = useUiStore((state) => state.mailListDensity);
  const senderSubjectPreference = useUiStore((state) => state.mailSenderSubjectLayout);
  const remoteImagePolicy = useUiStore((state) => state.remoteImagePolicy);
  const mailColumnOrder = useUiStore((state) => state.mailColumnOrder);
  const hiddenMailColumns = useUiStore((state) => state.hiddenMailColumns);
  const mailColumnWidths = useUiStore((state) => state.mailColumnWidths);
  const savedMailSearches = useUiStore((state) => state.savedMailSearches);
  const narrowScreen = useMediaQuery('(max-width: 680px)');
  const senderSubjectLayout = resolveSenderSubjectLayout(
    senderSubjectPreference,
    narrowScreen,
  );
  const rowHeight =
    senderSubjectLayout === 'stacked'
      ? STACKED_ROW_HEIGHTS[density]
      : narrowScreen
        ? Math.max(48, ROW_HEIGHTS[density])
        : ROW_HEIGHTS[density];
  const headerHeight = HEADER_HEIGHTS[density];
  const isSearch = props.searchQuery !== undefined;
  const searchQuery = props.searchQuery ?? '';
  const searchSort = isSearch ? props.searchSort : 'newest';
  const scope: MailScope = props.scope ?? 'unified';
  const folderScope = typeof scope === 'object' ? scope : null;
  const snoozedView = scope === 'snoozed';
  const prefetchThread = useCallback(
    (threadId: number) => {
      void prefetchThreadOpen(
        queryClient,
        threadId,
        remoteImageRequestMode(remoteImagePolicy),
      ).catch(() => undefined);
    },
    [queryClient, remoteImagePolicy],
  );

  // Unread-first sections apply to inboxes; Sent/Trash/etc. get a flat list.
  const sectioned =
    !isSearch &&
    (scope === 'unified' ||
      (folderScope !== null &&
        folders?.find((f) => f.id === folderScope.folderId)?.specialUse === 'inbox'));
  const currentSpecialUse: SpecialUse | null = isSearch
    ? null
    : scope === 'unified'
      ? 'inbox'
      : folderScope
        ? (folders?.find((folder) => folder.id === folderScope.folderId)?.specialUse ?? null)
        : null;
  const trashFolder =
    currentSpecialUse === 'trash' && folderScope
      ? (folders?.find((folder) => folder.id === folderScope.folderId) ?? null)
      : null;
  const trashAccount = trashFolder
    ? (accounts?.find((account) => account.id === trashFolder.accountId) ?? null)
    : null;
  const inboxFolder = trashFolder
    ? (folders?.find(
        (folder) =>
          folder.accountId === trashFolder.accountId && folder.specialUse === 'inbox',
      ) ?? null)
    : null;
  const navigationCounts = useMailNavigationCounts(sectioned);
  const scopedUnreadCount = navigationCounts.data
    ? scope === 'unified'
      ? navigationCounts.data.unifiedUnread
      : (navigationCounts.data.folders.find((count) => count.id === folderScope?.folderId)
          ?.unread ?? 0)
    : null;

  const unread = useUnreadList(scope, sectioned);
  const rest = useThreadList(scope, sectioned ? 'rest' : 'all', !isSearch);
  const searchResults = useSearchResults(
    searchQuery,
    searchSort,
    isSearch && searchQuery.trim().length > 0,
  );

  const [unreadCollapsed, setUnreadCollapsed] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [selectionAnchorId, setSelectionAnchorId] = useState<number | null>(null);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [permanentDelete, setPermanentDelete] = useState<PermanentDeleteRequest | null>(null);
  const [emptyTrashOpen, setEmptyTrashOpen] = useState(false);
  const [emptyTrashProgress, setEmptyTrashProgress] = useState<EmptyTrashProgress | null>(null);
  const [snoozeTargetIds, setSnoozeTargetIds] = useState<number[] | null>(null);
  const [matchingSelection, setMatchingSelection] = useState(false);
  const [savedSearchesOpen, setSavedSearchesOpen] = useState(false);

  const items = useMemo<ListItem[]>(() => {
    const out: ListItem[] = [];
    const unreadRows = unread.data?.rows ?? [];
    const restRows = isSearch
      ? (searchResults.data?.pages.flatMap((page) => page.rows) ?? [])
      : (rest.data?.pages.flatMap((page) => page.rows) ?? []);
    if (sectioned && (unreadRows.length > 0 || restRows.length > 0)) {
      out.push({
        kind: 'header',
        label: 'Unread',
        count: scopedUnreadCount ?? unreadRows.length,
        section: 'unread',
      });
      if (!unreadCollapsed) for (const row of unreadRows) out.push({ kind: 'thread', row });
      out.push({ kind: 'header', label: 'Everything else', count: 0, section: 'rest' });
    }
    for (const row of restRows) out.push({ kind: 'thread', row });
    if (isSearch ? searchResults.hasNextPage : rest.hasNextPage) {
      out.push({ kind: 'loader' });
    }
    return out;
  }, [
    isSearch,
    unread.data,
    rest.data,
    rest.hasNextPage,
    searchResults.data,
    searchResults.hasNextPage,
    scopedUnreadCount,
    sectioned,
    unreadCollapsed,
  ]);
  const threadRows = useMemo(
    () => items.flatMap((item) => (item.kind === 'thread' ? [item.row] : [])),
    [items],
  );
  const threadIds = useMemo(() => threadRows.map((row) => row.id), [threadRows]);
  const bulkThreadIds = useMemo(
    () => threadIds.slice(0, MAX_BULK_THREADS),
    [threadIds],
  );
  const selectedRows = useMemo(
    () => threadRows.filter((row) => selectedIds.has(row.id)),
    [selectedIds, threadRows],
  );
  const savedCurrentSearch =
    isSearch &&
    searchQuery.trim().length > 0 &&
    savedMailSearches.some(
      (search) => search.query === searchQuery.trim() && search.sort === searchSort,
    );
  const searchPresentation = useMemo(
    () => describeSearch(searchQuery),
    [searchQuery],
  );
  const effectiveSearchSort =
    searchResults.data?.pages[0]?.sort ??
    (searchPresentation.hasTextTerms ? searchSort : 'newest');
  const updateSearch = (query: string, sort: MailSearchSort = searchSort) => {
    void navigate({
      to: '/mail/search',
      search: { q: query, sort },
    });
  };
  const matchingSearchIsStarred = /(^|\s)is:starred(?=\s|$)/i.test(searchQuery);
  const contextMenuRow = contextMenu
    ? (threadRows.find((row) => row.id === contextMenu.rowId) ?? null)
    : null;
  const contextTargetRows = contextMenu
    ? threadRows.filter((row) => contextMenu.targetIds.includes(row.id))
    : [];

  useEffect(() => {
    const available = new Set(threadIds);
    if (!action.isPending) {
      setSelectedIds((previous) => {
        if ([...previous].every((id) => available.has(id))) return previous;
        return new Set([...previous].filter((id) => available.has(id)));
      });
      if (activeId !== null && !available.has(activeId)) setActiveId(null);
      if (selectionAnchorId !== null && !available.has(selectionAnchorId)) {
        setSelectionAnchorId(null);
      }
    }
  }, [action.isPending, activeId, selectionAnchorId, threadIds]);

  useEffect(() => {
    if (contextMenu && !threadIds.includes(contextMenu.rowId)) setContextMenu(null);
  }, [contextMenu, threadIds]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (i) => (items[i]?.kind === 'header' ? headerHeight : rowHeight),
    overscan: 12,
  });

  useEffect(() => virtualizer.measure(), [headerHeight, rowHeight, virtualizer]);

  const virtualItems = virtualizer.getVirtualItems();
  const hasNextPage = isSearch ? searchResults.hasNextPage : rest.hasNextPage;
  const isFetchingNextPage = isSearch
    ? searchResults.isFetchingNextPage
    : rest.isFetchingNextPage;
  const fetchNextPage = isSearch ? searchResults.fetchNextPage : rest.fetchNextPage;
  useEffect(() => {
    const last = virtualItems[virtualItems.length - 1];
    if (!last) return;
    if (last.index >= items.length - 10 && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, items.length, virtualItems]);

  const openThread = (row: DisplayThreadRow) => {
    prefetchThread(row.id);
    if (isSearch) {
      void navigate({
        to: '/mail/search/thread/$threadId',
        params: { threadId: String(row.id) },
        search: { q: searchQuery, sort: searchSort },
      });
    } else if (scope === 'unified') {
      void navigate({
        to: '/mail/unified/thread/$threadId',
        params: { threadId: String(row.id) },
      });
    } else if (scope === 'snoozed') {
      void navigate({
        to: '/mail/snoozed/thread/$threadId',
        params: { threadId: String(row.id) },
      });
    } else {
      void navigate({
        to: '/mail/a/$accountId/f/$folderId/thread/$threadId',
        params: {
          accountId: String(scope.accountId),
          folderId: String(scope.folderId),
          threadId: String(row.id),
        },
      });
    }
  };

  const threadHref = (row: DisplayThreadRow): string => {
    if (isSearch) {
      return `/mail/search/thread/${row.id}?${new URLSearchParams({ q: searchQuery, sort: searchSort })}`;
    }
    if (scope === 'unified') return `/mail/unified/thread/${row.id}`;
    if (scope === 'snoozed') return `/mail/snoozed/thread/${row.id}`;
    return `/mail/a/${scope.accountId}/f/${scope.folderId}/thread/${row.id}`;
  };

  const perform = async (
    targetIds: number[],
    actionName: MessageActionRequest['action'],
    successMessage: string | null = null,
    targetFolderId?: number,
  ) => {
    if (matchingSelection && isSearch) {
      if (actionName === 'delete_perm' || actionName === 'move') return;
      await action.runSearch({
        q: searchQuery,
        action: actionName,
      });
      clearSelection();
      return;
    }
    if (targetIds.length === 0) return;
    if (actionName === 'move' && targetFolderId === undefined) {
      toast.error('Choose a destination folder before moving mail.');
      return;
    }
    const moving =
      actionName === 'archive' ||
      actionName === 'trash' ||
      actionName === 'spam' ||
      actionName === 'not_spam' ||
      actionName === 'move';
    const removing = moving || actionName === 'delete_perm';
    const folderScoped = removing;
    const result = await action.run(
      {
        threadIds: targetIds,
        action: actionName,
        ...(folderScoped && folderScope ? { sourceFolderId: folderScope.folderId } : {}),
        ...(actionName === 'move' ? { targetFolderId } : {}),
      },
      successMessage,
    );
    if (removing && result.affectedMessageIds.length > 0) {
      const moved = new Set(targetIds);
      setSelectedIds((previous) => new Set([...previous].filter((id) => !moved.has(id))));
      if (activeId !== null && moved.has(activeId)) setActiveId(null);
    }
  };

  const requestAction = (
    targetIds: number[],
    actionName: MessageActionRequest['action'],
    successMessage: string | null = null,
  ) => {
    if (actionName === 'delete_perm') {
      setContextMenu(null);
      setPermanentDelete({ targetIds });
      return;
    }
    void perform(targetIds, actionName, successMessage).catch(() => undefined);
  };

  const requestSnooze = (targetIds: number[]) => {
    if (targetIds.length === 0) return;
    setContextMenu(null);
    setSnoozeTargetIds(targetIds);
  };

  const returnToInbox = (targetIds: number[]) => {
    if (targetIds.length === 0 || unsnooze.isPending) return;
    setContextMenu(null);
    void unsnooze
      .mutateAsync(targetIds)
      .then(() => {
        clearSelection();
        toast.success(targetIds.length === 1 ? 'Conversation returned to inbox' : 'Conversations returned to inbox');
      })
      .catch((error) => toast.error(errorMessage(error)));
  };

  const toggleSelection = (id: number, range: boolean) => {
    setMatchingSelection(false);
    setActiveId(id);
    setSelectedIds((previous) => {
      const next = new Set(previous);
      const anchorIndex = selectionAnchorId === null ? -1 : threadIds.indexOf(selectionAnchorId);
      const targetIndex = threadIds.indexOf(id);
      if (range && anchorIndex >= 0 && targetIndex >= 0) {
        const shouldSelect = !previous.has(id);
        const start = Math.min(anchorIndex, targetIndex);
        const end = Math.max(anchorIndex, targetIndex);
        for (const rangeId of threadIds.slice(start, end + 1)) {
          if (shouldSelect) next.add(rangeId);
          else next.delete(rangeId);
        }
      } else if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      if (next.size <= MAX_BULK_THREADS) return next;
      toast.info(`Bulk actions are limited to ${MAX_BULK_THREADS} conversations at a time.`, {
        id: 'bulk-selection-limit',
      });
      return new Set([...next].slice(0, MAX_BULK_THREADS));
    });
    if (!range || selectionAnchorId === null) setSelectionAnchorId(id);
  };

  const toggleAllLoaded = () => {
    if (matchingSelection) {
      clearSelection();
      return;
    }
    const allSelected =
      bulkThreadIds.length > 0 && bulkThreadIds.every((id) => selectedIds.has(id));
    if (!allSelected && threadIds.length > MAX_BULK_THREADS) {
      toast.info(`Selected the first ${MAX_BULK_THREADS} loaded conversations.`, {
        id: 'bulk-selection-limit',
      });
    }
    setSelectedIds(
      allSelected ? new Set() : new Set(bulkThreadIds),
    );
    setSelectionAnchorId(allSelected ? null : (bulkThreadIds[0] ?? null));
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectionAnchorId(null);
    setMatchingSelection(false);
  };

  const openContextMenu = (row: ThreadRow, x: number, y: number) => {
    setMatchingSelection(false);
    const rowAlreadySelected = selectedIds.has(row.id);
    const targetIds = rowAlreadySelected ? selectedRows.map((selected) => selected.id) : [row.id];
    if (!rowAlreadySelected) {
      setSelectedIds(new Set([row.id]));
      setSelectionAnchorId(row.id);
    }
    setActiveId(row.id);
    setContextMenu({ x, y, rowId: row.id, targetIds });
  };

  const moveToInbox = (targetIds: number[]) => {
    if (!inboxFolder) {
      toast.error('This account does not have an Inbox folder.');
      return;
    }
    void perform(
      targetIds,
      'move',
      targetIds.length === 1
        ? 'Moved conversation to Inbox'
        : `Moved ${targetIds.length.toLocaleString()} conversations to Inbox`,
      inboxFolder.id,
    ).catch(() => undefined);
  };

  const startThreadDrag = (event: DragEvent<HTMLDivElement>, row: DisplayThreadRow) => {
    if (action.isPending || matchingSelection) {
      event.preventDefault();
      if (matchingSelection) {
        toast.info('Drag loaded conversations only; clear the select-all-matching mode first.', {
          id: 'mail-drag-matching-selection',
        });
      }
      return;
    }
    const rows = selectedIds.has(row.id) ? selectedRows : [row];
    if (rows.some((candidate) => candidate.accountId !== row.accountId)) {
      event.preventDefault();
      toast.error('Selected conversations span accounts. Move one account at a time.', {
        id: 'mail-drag-account-mismatch',
      });
      return;
    }
    setActiveId(row.id);
    beginMailDrag(event.dataTransfer, {
      accountId: row.accountId,
      threadIds: rows.map((candidate) => candidate.id),
      ...(folderScope?.accountId === row.accountId
        ? { sourceFolderId: folderScope.folderId }
        : {}),
    });
  };

  const openActiveContextMenu = () => {
    const row = threadRows.find((candidate) => candidate.id === activeId) ?? selectedRows[0];
    if (!row) return;
    const element = document.querySelector<HTMLElement>(`[data-thread-id="${row.id}"]`);
    const bounds = element?.getBoundingClientRect();
    openContextMenu(
      row,
      bounds ? bounds.left + 48 : window.innerWidth / 2,
      bounds ? bounds.top + rowHeight / 2 : window.innerHeight / 2,
    );
  };

  const moveActive = (direction: -1 | 1) => {
    if (threadRows.length === 0) return;
    const current = activeId === null ? -1 : threadRows.findIndex((row) => row.id === activeId);
    const nextIndex =
      current < 0
        ? direction === 1
          ? 0
          : threadRows.length - 1
        : Math.max(0, Math.min(threadRows.length - 1, current + direction));
    const next = threadRows[nextIndex]!;
    setActiveId(next.id);
    const itemIndex = items.findIndex((item) => item.kind === 'thread' && item.row.id === next.id);
    if (itemIndex >= 0) virtualizer.scrollToIndex(itemIndex, { align: 'auto' });
  };

  const keyboardTargets = () => {
    if (selectedRows.length > 0) return selectedRows;
    const active = threadRows.find((row) => row.id === activeId);
    return active ? [active] : [];
  };

  const runKeyboardAction = (actionName: MessageActionRequest['action']) => {
    if (action.isPending) return;
    if (matchingSelection && isSearch) {
      requestAction([], actionName);
      return;
    }
    const targets = keyboardTargets();
    if (targets.length === 0) return;
    if (
      actionName === 'archive' &&
      (currentSpecialUse === 'archive' || currentSpecialUse === 'all')
    ) {
      return;
    }
    if (actionName === 'trash' && currentSpecialUse === 'trash') return;
    requestAction(
      targets.map((row) => row.id),
      actionName,
      actionSuccessMessage(actionName, targets.length),
    );
  };

  useKeyboardShortcuts(
    {
      j: (event) => {
        event.preventDefault();
        moveActive(1);
      },
      k: (event) => {
        event.preventDefault();
        moveActive(-1);
      },
      o: (event) => {
        event.preventDefault();
        const row = threadRows.find((candidate) => candidate.id === activeId) ?? selectedRows[0];
        if (row) openThread(row);
      },
      Enter: (event) => {
        if (event.target instanceof Element && event.target.closest('a, button')) return;
        event.preventDefault();
        const row = threadRows.find((candidate) => candidate.id === activeId) ?? selectedRows[0];
        if (row) openThread(row);
      },
      x: (event) => {
        event.preventDefault();
        if (activeId !== null) toggleSelection(activeId, false);
      },
      s: (event) => {
        event.preventDefault();
        const targets = keyboardTargets();
        runKeyboardAction(
          matchingSelection
            ? matchingSearchIsStarred
              ? 'unstar'
              : 'star'
            : targets.length > 0 && targets.every((row) => row.starred)
              ? 'unstar'
              : 'star',
        );
      },
      e: (event) => {
        event.preventDefault();
        runKeyboardAction('archive');
      },
      '[Shift]+#': (event) => {
        event.preventDefault();
        runKeyboardAction(currentSpecialUse === 'trash' ? 'delete_perm' : 'trash');
      },
      'Shift+i': (event) => {
        event.preventDefault();
        runKeyboardAction('read');
      },
      'Shift+u': (event) => {
        event.preventDefault();
        runKeyboardAction('unread');
      },
      'Shift+F10': (event) => {
        event.preventDefault();
        openActiveContextMenu();
      },
      ContextMenu: (event) => {
        event.preventDefault();
        openActiveContextMenu();
      },
      Escape: () => clearSelection(),
    },
    {
      enabled:
        keyboardEnabled &&
        threadRows.length > 0 &&
        contextMenu === null &&
        permanentDelete === null &&
        snoozeTargetIds === null &&
        !emptyTrashOpen,
    },
  );

  const loading = isSearch
    ? searchQuery.trim().length > 0 && searchResults.isPending
    : (sectioned && unread.isPending) || rest.isPending;
  const listError =
    !isSearch && sectioned && unread.isError
      ? unread.error
      : !isSearch && rest.isError
        ? rest.error
        : null;
  const empty = !loading && items.filter((i) => i.kind === 'thread').length === 0;

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-7 animate-spin text-ink-muted" />
      </div>
    );
  }

  if (isSearch && searchResults.isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
        <SearchIcon className="size-14 text-ink-muted/40" />
        <p className="text-lg font-medium">Search could not run</p>
        <p className="max-w-lg text-sm text-danger">
          {searchResults.error instanceof Error
            ? searchResults.error.message
            : 'The search request failed.'}
        </p>
      </div>
    );
  }

  if (listError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
        <AlertTriangle className="size-14 text-danger/60" />
        <p className="text-lg font-medium">Mail could not load</p>
        <p className="max-w-lg text-sm text-danger">
          {listError instanceof Error
            ? listError.message
            : 'The mailbox request failed.'}
        </p>
        <button
          type="button"
          onClick={() => {
            if (sectioned) void unread.refetch();
            void rest.refetch();
          }}
          className="inline-flex items-center gap-2 rounded-full border border-line px-4 py-2 text-sm font-medium hover:bg-hover"
        >
          <RefreshCw className="size-4" />
          Try again
        </button>
      </div>
    );
  }

  if (empty && !emptyTrashOpen) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
        {isSearch && searchQuery.trim() ? (
          <SearchResultsControls
            query={searchQuery}
            requestedSort={searchSort}
            effectiveSort={effectiveSearchSort}
            hasTextTerms={searchPresentation.hasTextTerms}
            hasPrefixes={searchPresentation.hasPrefixes}
            broadenedQuery={searchPresentation.broadenedQuery}
            exactQuery={searchPresentation.exactQuery}
            loadedCount={0}
            onChangeQuery={updateSearch}
            onChangeSort={(sort) => updateSearch(searchQuery, sort)}
            compact
          />
        ) : null}
        {isSearch ? (
          <SearchIcon className="size-14 text-ink-muted/40" />
        ) : snoozedView ? (
          <Clock3 className="size-14 text-ink-muted/40" />
        ) : (
          <Inbox className="size-14 text-ink-muted/40" />
        )}
        <p className="text-lg font-medium">
          {isSearch
            ? searchQuery.trim()
              ? 'No matching messages'
              : 'Search your mail'
            : snoozedView
              ? 'Nothing snoozed'
              : 'Nothing here'}
        </p>
        <p className="max-w-sm text-sm text-ink-muted">
          {isSearch
            ? searchQuery.trim()
              ? `No messages matched “${searchQuery}”. Try fewer terms or different operators.`
              : 'Enter words or operators such as from:, subject:, is:unread, or has:attachment.'
            : snoozedView
              ? 'Snoozed conversations return to your inbox automatically at their selected time.'
              : accounts && accounts.length === 0
              ? 'Add an IMAP account in Settings to start syncing.'
              : 'No messages in this view yet — sync may still be running.'}
        </p>
        {isSearch && searchQuery.trim() ? (
          <button
            type="button"
            onClick={() => setSavedSearchesOpen(true)}
            className="rounded-full border border-line px-4 py-2 text-sm font-medium hover:bg-hover"
          >
            {savedCurrentSearch ? 'Manage saved search' : 'Save this search'}
          </button>
        ) : null}
        {savedSearchesOpen ? (
          <SavedSearchesModal
            query={searchQuery}
            sort={searchSort}
            onClose={() => setSavedSearchesOpen(false)}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <MessageToolbar
        selectedRows={selectedRows}
        allLoadedSelected={
          bulkThreadIds.length > 0 && bulkThreadIds.every((id) => selectedIds.has(id))
        }
        canSelectAll={bulkThreadIds.length > 0}
        currentSpecialUse={currentSpecialUse}
        pending={action.isPending || snooze.isPending || unsnooze.isPending}
        matchingSelection={matchingSelection}
        matchingAllStarred={matchingSearchIsStarred}
        onToggleAll={toggleAllLoaded}
        onClear={clearSelection}
        onAction={(actionName) => {
          requestAction(
            selectedRows.map((row) => row.id),
            actionName,
            actionSuccessMessage(actionName, selectedRows.length),
          );
        }}
        onMoveToInbox={
          currentSpecialUse === 'trash' && inboxFolder
            ? () => moveToInbox(selectedRows.map((row) => row.id))
            : undefined
        }
        snoozedView={snoozedView}
        onSnooze={() => requestSnooze(selectedRows.map((row) => row.id))}
        onUnsnooze={() => returnToInbox(selectedRows.map((row) => row.id))}
        savedSearch={
          isSearch && searchQuery.trim()
            ? {
                saved: savedCurrentSearch,
                onManage: () => setSavedSearchesOpen(true),
              }
            : undefined
        }
        trash={
          trashFolder
            ? {
                count: trashFolder.totalCount,
                pending: emptyTrash.isPending,
                onEmpty: () => {
                  emptyTrash.reset();
                  setEmptyTrashProgress(null);
                  setEmptyTrashOpen(true);
                },
              }
            : undefined
        }
      />
      {isSearch && searchQuery.trim() ? (
        <SearchResultsControls
          query={searchQuery}
          requestedSort={searchSort}
          effectiveSort={effectiveSearchSort}
          hasTextTerms={searchPresentation.hasTextTerms}
          hasPrefixes={searchPresentation.hasPrefixes}
          broadenedQuery={searchPresentation.broadenedQuery}
          exactQuery={searchPresentation.exactQuery}
          loadedCount={threadIds.length}
          onChangeQuery={updateSearch}
          onChangeSort={(sort) => updateSearch(searchQuery, sort)}
        />
      ) : null}
      {isSearch && matchingSelection ? (
        <div className="flex shrink-0 flex-wrap items-center justify-center gap-2 border-b border-line bg-accent-soft px-3 py-2 text-center text-sm">
          <span>
            Up to {MAX_SEARCH_BULK_THREADS.toLocaleString()} conversations matching this search
            will be changed. The server rechecks the query when you choose an action.
          </span>
          <button
            type="button"
            onClick={clearSelection}
            className="rounded-full px-3 py-1 font-semibold text-accent hover:bg-surface/70"
          >
            Clear
          </button>
        </div>
      ) : isSearch &&
        (searchResults.hasNextPage || threadIds.length > MAX_SEARCH_BULK_THREADS) &&
        bulkThreadIds.length > 0 &&
        bulkThreadIds.every((id) => selectedIds.has(id)) ? (
        <div className="flex shrink-0 flex-wrap items-center justify-center gap-2 border-b border-line bg-accent-soft px-3 py-2 text-center text-sm">
          <span>
            {threadIds.length > MAX_SEARCH_BULK_THREADS ? 'The first' : 'All'}{' '}
            {bulkThreadIds.length.toLocaleString()} loaded conversations are selected.
          </span>
          <button
            type="button"
            onClick={() => setMatchingSelection(true)}
            className="rounded-full px-3 py-1 font-semibold text-accent hover:bg-surface/70"
          >
            Select up to {MAX_SEARCH_BULK_THREADS.toLocaleString()} matching this search
          </button>
        </div>
      ) : null}
      <div
        ref={scrollRef}
        className={`mail-list-container min-h-0 flex-1 overflow-y-auto ${
          senderSubjectLayout === 'stacked'
            ? 'mail-list-sender-subject-stacked'
            : 'mail-list-sender-subject-horizontal'
        }`}
      >
        <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
          {virtualItems.map((vi) => {
            const item = items[vi.index]!;
            return (
              <div
                key={vi.key}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: vi.size,
                  transform: `translateY(${vi.start}px)`,
                }}
              >
                {item.kind === 'header' ? (
                  <SectionHeader
                    item={item}
                    collapsed={unreadCollapsed}
                    onToggle={() => setUnreadCollapsed((v) => !v)}
                  />
                ) : item.kind === 'thread' ? (
                  <ThreadRowView
                    row={item.row}
                    scope={scope}
                    currentSpecialUse={currentSpecialUse}
                    searchQuery={isSearch ? searchQuery : undefined}
                    searchSort={isSearch ? searchSort : undefined}
                    accountIdentityMode={accountIdentityMode}
                    columns={mailColumnOrder}
                    hiddenColumns={hiddenMailColumns}
                    widths={mailColumnWidths}
                    density={density}
                    stackSenderSubject={senderSubjectLayout === 'stacked'}
                    dragEnabled={!narrowScreen}
                    selected={selectedIds.has(item.row.id)}
                    active={activeId === item.row.id}
                    previewed={props.previewThreadId === item.row.id}
                    pending={action.isPending}
                    onActivate={() => setActiveId(item.row.id)}
                    onPrefetch={() => prefetchThread(item.row.id)}
                    onToggleSelection={(range) => toggleSelection(item.row.id, range)}
                    onContextMenu={(x, y) => openContextMenu(item.row, x, y)}
                    onDragStart={(event) => startThreadDrag(event, item.row)}
                    onDragEnd={endMailDrag}
                    onAction={(actionName, successMessage) => {
                      requestAction([item.row.id], actionName, successMessage);
                    }}
                    onMoveToInbox={
                      currentSpecialUse === 'trash' && inboxFolder
                        ? () => moveToInbox([item.row.id])
                        : undefined
                    }
                  />
                ) : (
                  <div className="flex items-center justify-center py-2">
                    <Loader2 className="size-4 animate-spin text-ink-muted" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {contextMenu && contextMenuRow && contextTargetRows.length > 0 ? (
        <MessageContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          row={contextMenuRow}
          targetRows={contextTargetRows}
          currentSpecialUse={currentSpecialUse}
          pending={action.isPending || snooze.isPending || unsnooze.isPending}
          onClose={() => setContextMenu(null)}
          onOpen={() => openThread(contextMenuRow)}
          onOpenNewTab={() => {
            window.open(threadHref(contextMenuRow), '_blank', 'noopener,noreferrer');
          }}
          onAction={(actionName) => {
            const targetIds = contextTargetRows.map((row) => row.id);
            requestAction(
              targetIds,
              actionName,
              actionSuccessMessage(actionName, targetIds.length),
            );
          }}
          onMoveToInbox={
            currentSpecialUse === 'trash' && inboxFolder
              ? () => moveToInbox(contextTargetRows.map((row) => row.id))
              : undefined
          }
          snoozedView={snoozedView}
          onSnooze={() => requestSnooze(contextTargetRows.map((row) => row.id))}
          onUnsnooze={() => returnToInbox(contextTargetRows.map((row) => row.id))}
        />
      ) : null}
      {permanentDelete ? (
        <PermanentDeleteDialog
          count={permanentDelete.targetIds.length}
          pending={action.isPending}
          onCancel={() => setPermanentDelete(null)}
          onConfirm={() => {
            void perform(
              permanentDelete.targetIds,
              'delete_perm',
              actionSuccessMessage('delete_perm', permanentDelete.targetIds.length),
            )
              .then(() => setPermanentDelete(null))
              .catch(() => undefined);
          }}
        />
      ) : null}
      {emptyTrashOpen && trashFolder ? (
        <EmptyTrashDialog
          accountName={trashAccount?.name ?? 'this account'}
          folderName={trashFolder.name}
          estimatedCount={trashFolder.totalCount}
          progress={emptyTrashProgress}
          pending={emptyTrash.isPending}
          onCancel={() => {
            setEmptyTrashOpen(false);
            setEmptyTrashProgress(null);
          }}
          onConfirm={() => {
            void emptyTrash
              .mutateAsync({
                folderId: trashFolder.id,
                onProgress: setEmptyTrashProgress,
              })
              .then((result) => {
                setEmptyTrashOpen(false);
                setEmptyTrashProgress(null);
                if (result.deletedMessageCount === 0) {
                  toast.info('Trash is already empty.');
                } else if (result.complete) {
                  toast.success(
                    `${result.deletedMessageCount.toLocaleString()} ${result.deletedMessageCount === 1 ? 'message' : 'messages'} removed from Trash. Provider deletion is syncing.`,
                    { duration: 10_000 },
                  );
                } else {
                  toast.warning(
                    `${result.deletedMessageCount.toLocaleString()} messages queued. ${result.remainingMessageCount.toLocaleString()} remain; run Empty Trash again to continue.`,
                    { duration: 12_000 },
                  );
                }
              })
              .catch((error) => toast.error(emptyTrashError(error), { duration: 12_000 }));
          }}
        />
      ) : null}
      {snoozeTargetIds ? (
        <SnoozeDialog
          count={snoozeTargetIds.length}
          pending={snooze.isPending}
          onClose={() => setSnoozeTargetIds(null)}
          onConfirm={(until) => {
            void snooze
              .mutateAsync({ threadIds: snoozeTargetIds, until })
              .then(() => {
                setSnoozeTargetIds(null);
                clearSelection();
                toast.success(
                  `Snoozed until ${new Date(until).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}`,
                );
              })
              .catch((error) => toast.error(errorMessage(error)));
          }}
        />
      ) : null}
      {savedSearchesOpen ? (
        <SavedSearchesModal
          query={searchQuery}
          sort={searchSort}
          onClose={() => setSavedSearchesOpen(false)}
        />
      ) : null}
    </div>
  );
}

function SavedSearchesModal({
  query,
  sort,
  onClose,
}: {
  query: string;
  sort: MailSearchSort;
  onClose: () => void;
}) {
  return (
    <Suspense
      fallback={
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 p-4"
          role="status"
        >
          <span className="flex items-center gap-2 rounded-full bg-surface px-4 py-2 text-sm text-ink-muted shadow-xl">
            <Loader2 className="size-4 animate-spin" /> Loading saved searches…
          </span>
        </div>
      }
    >
      <SavedSearchesDialog initialQuery={query} initialSort={sort} onClose={onClose} />
    </Suspense>
  );
}

function SearchResultsControls({
  query,
  requestedSort,
  effectiveSort,
  hasTextTerms,
  hasPrefixes,
  broadenedQuery,
  exactQuery,
  loadedCount,
  onChangeQuery,
  onChangeSort,
  compact = false,
}: {
  query: string;
  requestedSort: MailSearchSort;
  effectiveSort: MailSearchSort;
  hasTextTerms: boolean;
  hasPrefixes: boolean;
  broadenedQuery: string | null;
  exactQuery: string | null;
  loadedCount: number;
  onChangeQuery: (query: string) => void;
  onChangeSort: (sort: MailSearchSort) => void;
  compact?: boolean;
}) {
  const variantQuery = hasPrefixes ? exactQuery : broadenedQuery;
  const variantLabel = hasPrefixes ? 'Use exact words' : 'Broaden with prefixes';
  const sortButton = (sort: MailSearchSort, label: string) => (
    <button
      type="button"
      onClick={() => onChangeSort(sort)}
      disabled={sort === 'relevance' && !hasTextTerms}
      aria-pressed={effectiveSort === sort}
      className={`rounded-full px-2.5 py-1 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-40 ${
        effectiveSort === sort ? 'bg-accent text-white' : 'hover:bg-hover'
      }`}
      title={
        sort === 'relevance' && !hasTextTerms
          ? 'Relevance needs at least one ordinary search word'
          : `Sort matching conversations by ${label.toLocaleLowerCase()}`
      }
    >
      {label}
    </button>
  );

  return (
    <div
      className={`${compact ? 'max-w-2xl rounded-xl border border-line bg-surface px-3 py-2' : 'border-b border-line bg-app px-3 py-2'} flex shrink-0 flex-wrap items-center justify-center gap-x-3 gap-y-2 text-xs text-ink-muted sm:justify-start`}
      title={`Search: ${query}`}
    >
      <span className="font-medium text-ink">
        {hasPrefixes ? 'Explicit prefix matching' : 'Exact words · all terms required'}
      </span>
      <span>{loadedCount.toLocaleString()} loaded</span>
      <span className="inline-flex rounded-full border border-line bg-surface p-0.5" aria-label="Search result order">
        {sortButton('relevance', 'Relevant')}
        {sortButton('newest', 'Newest')}
      </span>
      {variantQuery && variantQuery !== query.trim() ? (
        <button
          type="button"
          onClick={() => onChangeQuery(variantQuery)}
          className="rounded-full border border-line px-3 py-1 font-medium text-ink hover:bg-hover"
        >
          {variantLabel}
        </button>
      ) : null}
      {!hasTextTerms && requestedSort === 'relevance' ? (
        <span>Operator-only searches use newest first.</span>
      ) : null}
      <Link
        to="/help"
        search={{ topic: 'mail-searches' }}
        className="font-medium text-accent hover:underline sm:ml-auto"
      >
        Search help
      </Link>
    </div>
  );
}

function describeSearch(query: string): {
  hasTextTerms: boolean;
  hasPrefixes: boolean;
  broadenedQuery: string | null;
  exactQuery: string | null;
} {
  try {
    const syntax = parseMailSearchSyntax(query);
    const hasTextTerms = syntax.textClauses.length > 0;
    const hasPrefixes = hasPrefixMailSearchTerms(query);
    return {
      hasTextTerms,
      hasPrefixes,
      broadenedQuery: hasTextTerms ? broadenMailSearchQuery(query) : null,
      exactQuery: hasPrefixes ? narrowMailSearchQuery(query) : null,
    };
  } catch {
    return {
      hasTextTerms: false,
      hasPrefixes: false,
      broadenedQuery: null,
      exactQuery: null,
    };
  }
}

function HighlightedSearchText({
  text,
  query,
}: {
  text: string;
  query?: string;
}) {
  if (!query || !text) return text;
  let terms: MailSearchTextTerm[];
  try {
    terms = mailSearchTextTerms(query);
  } catch {
    return text;
  }
  const expression = searchHighlightExpression(terms);
  if (!expression) return text;
  const parts: ReactNode[] = [];
  let cursor = 0;
  for (const match of text.matchAll(expression)) {
    if (match.index === undefined || !match[0]) continue;
    if (match.index > cursor) parts.push(text.slice(cursor, match.index));
    parts.push(
      <mark
        key={`${match.index}:${match[0]}`}
        className="rounded-sm bg-amber-200 px-0.5 text-inherit dark:bg-amber-500/35"
      >
        {match[0]}
      </mark>,
    );
    cursor = match.index + match[0].length;
  }
  if (parts.length === 0) return text;
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
}

function searchHighlightExpression(terms: readonly MailSearchTextTerm[]): RegExp | null {
  const alternatives = terms.flatMap((term) => {
    const phrase = term.value
      .trim()
      .split(/\s+/u)
      .map(escapeSearchRegExp)
      .join('\\s+');
    return phrase
      ? [`(?<![\\p{L}\\p{N}_])${phrase}${term.prefix ? '[\\p{L}\\p{N}_]*' : ''}(?![\\p{L}\\p{N}_])`]
      : [];
  });
  if (alternatives.length === 0) return null;
  try {
    return new RegExp(alternatives.join('|'), 'giu');
  } catch {
    return null;
  }
}

function escapeSearchRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&');
}

function searchFieldLabel(field: MailSearchMatch['field']): string {
  switch (field) {
    case 'subject':
      return 'Subject';
    case 'sender':
      return 'Sender';
    case 'recipient':
      return 'Recipient';
    case 'body':
      return 'Message';
  }
}

function SectionHeader({
  item,
  collapsed,
  onToggle,
}: {
  item: Extract<ListItem, { kind: 'header' }>;
  collapsed: boolean;
  onToggle: () => void;
}) {
  const isUnread = item.section === 'unread';
  return (
    <button
      onClick={isUnread ? onToggle : undefined}
      className={`flex h-full w-full items-center gap-1 border-b border-line bg-app px-4 text-xs font-semibold text-ink-muted ${isUnread ? 'cursor-pointer hover:text-ink' : 'cursor-default'}`}
    >
      {isUnread ? (
        collapsed ? (
          <ChevronRight className="size-3.5" />
        ) : (
          <ChevronDown className="size-3.5" />
        )
      ) : null}
      {item.label}
      {isUnread && item.count > 0 ? <span className="ml-1">({item.count})</span> : null}
    </button>
  );
}

function ThreadRowView({
  row,
  scope,
  currentSpecialUse,
  searchQuery,
  searchSort,
  accountIdentityMode,
  columns,
  hiddenColumns,
  widths,
  density,
  stackSenderSubject,
  dragEnabled,
  selected,
  active,
  previewed,
  pending,
  onActivate,
  onPrefetch,
  onToggleSelection,
  onContextMenu,
  onDragStart,
  onDragEnd,
  onAction,
  onMoveToInbox,
}: {
  row: DisplayThreadRow;
  scope: MailScope;
  currentSpecialUse: SpecialUse | null;
  searchQuery?: string;
  searchSort?: MailSearchSort;
  accountIdentityMode: AccountIdentityMode;
  columns: MailColumn[];
  hiddenColumns: Array<Exclude<MailColumn, 'subject'>>;
  widths: MailColumnWidths;
  density: MailListDensity;
  stackSenderSubject: boolean;
  dragEnabled: boolean;
  selected: boolean;
  active: boolean;
  previewed: boolean;
  pending: boolean;
  onActivate: () => void;
  onPrefetch: () => void;
  onToggleSelection: (range: boolean) => void;
  onContextMenu: (x: number, y: number) => void;
  onDragStart: (event: DragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
  onAction: (
    action: MessageActionRequest['action'],
    successMessage?: string | null,
  ) => void;
  onMoveToInbox?: (() => void) | undefined;
}) {
  const { data: accounts } = useAccounts();
  const prefetchTimer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (prefetchTimer.current !== null) window.clearTimeout(prefetchTimer.current);
    },
    [row.id],
  );
  const cancelScheduledPrefetch = () => {
    if (prefetchTimer.current === null) return;
    window.clearTimeout(prefetchTimer.current);
    prefetchTimer.current = null;
  };
  const prefetchNow = () => {
    cancelScheduledPrefetch();
    onPrefetch();
  };
  const schedulePrefetch = () => {
    if (prefetchTimer.current !== null) return;
    prefetchTimer.current = window.setTimeout(() => {
      prefetchTimer.current = null;
      onPrefetch();
    }, 150);
  };
  const account = accounts?.find((a) => a.id === row.accountId);
  const match = searchQuery !== undefined ? (row.match ?? null) : null;
  const senderText = match?.senderLabel || participantsLabel(row);
  const subjectText = match?.subject || row.subject || '(no subject)';
  const snippetText = match?.snippet ?? row.snippet;
  const matchContext = match
    ? `${match.isLatestMessage ? '' : 'Older message · '}${searchFieldLabel(match.field)}`
    : null;

  const linkProps =
    searchQuery !== undefined
      ? {
          to: '/mail/search/thread/$threadId' as const,
          params: { threadId: String(row.id) },
          search: { q: searchQuery, sort: searchSort ?? 'newest' },
        }
      : scope === 'unified'
        ? { to: '/mail/unified/thread/$threadId' as const, params: { threadId: String(row.id) } }
        : scope === 'snoozed'
          ? { to: '/mail/snoozed/thread/$threadId' as const, params: { threadId: String(row.id) } }
        : {
            to: '/mail/a/$accountId/f/$folderId/thread/$threadId' as const,
            params: {
              accountId: String(scope.accountId),
              folderId: String(scope.folderId),
              threadId: String(row.id),
            },
          };

  const actionButton =
    'rounded-full p-1.5 text-ink-muted hover:bg-hover hover:text-ink disabled:opacity-40';
  const visibleColumns = columns.filter(
    (column) => column === 'subject' || !hiddenColumns.some((hidden) => hidden === column),
  );
  const stackedPrimaryColumn = stackSenderSubject
    ? visibleColumns.find((column) => column === 'sender' || column === 'subject')
    : undefined;
  const showStackedPrimary =
    stackSenderSubject &&
    visibleColumns.includes('sender') &&
    visibleColumns.includes('subject');

  const renderColumn = (column: MailColumn): ReactNode => {
    const flexBasis = column === 'account' && accountIdentityMode === 'color' ? 16 : widths[column];
    switch (column) {
      case 'account':
        return (scope === 'unified' || scope === 'snoozed') && account ? (
          <span
            key={column}
            className="mail-column mail-column-account min-w-0 shrink-0 overflow-hidden font-normal"
            style={{ flexBasis }}
          >
            <AccountIdentity account={account} mode={accountIdentityMode} />
          </span>
        ) : null;
      case 'sender':
        return (
          <span
            key={column}
            className="mail-column mail-column-sender min-w-0 shrink-0 truncate"
            style={{ flexBasis }}
            title={senderText}
          >
            <HighlightedSearchText text={senderText} query={searchQuery} />
          </span>
        );
      case 'subject':
        return (
          <span
            key={column}
            className="mail-column mail-column-subject min-w-32 flex-1 truncate"
            style={{ flexBasis }}
            title={subjectText}
          >
            <HighlightedSearchText text={subjectText} query={searchQuery} />
          </span>
        );
      case 'snippet':
        return (
          <span
            key={column}
            className="mail-column mail-column-snippet min-w-28 flex-1 truncate font-normal text-ink-muted"
            style={{ flexBasis }}
            title={match ? `${matchContext}: ${snippetText}` : (row.snippet || undefined)}
          >
            {matchContext ? (
              <span className="mr-1 rounded bg-accent-soft px-1 py-0.5 text-[0.68rem] font-medium text-accent">
                {matchContext}
              </span>
            ) : null}
            <HighlightedSearchText text={snippetText} query={searchQuery} />
          </span>
        );
      case 'attachment':
        return (
          <span
            key={column}
            className="mail-column mail-column-attachment grid shrink-0 place-items-center"
            style={{ flexBasis }}
          >
            {row.hasAttachment ? (
              <Paperclip className="size-4 text-ink-muted" aria-label="Has attachment" />
            ) : null}
          </span>
        );
      case 'date': {
        const displayDate =
          scope === 'snoozed' && row.snoozedUntil
            ? row.snoozedUntil
            : (match?.date ?? row.lastMessageAt);
        const dateTitle =
          scope === 'snoozed'
            ? `Returns ${format(new Date(displayDate), 'PPpp')}`
            : match
              ? `Matching message: ${format(new Date(displayDate), 'PPpp')}; conversation last updated ${format(new Date(row.lastMessageAt), 'PPpp')}`
              : format(new Date(displayDate), 'PPpp');
        return (
          <span
            key={column}
            className="mail-column mail-column-date shrink-0 text-right text-xs font-normal"
            style={{ flexBasis }}
            title={dateTitle}
          >
            {scope === 'snoozed' ? <Clock3 className="mr-1 inline size-3" /> : null}
            {formatRowDate(displayDate)}
          </span>
        );
      }
    }
  };

  const renderVisibleColumn = (column: MailColumn): ReactNode => {
    if (!showStackedPrimary || (column !== 'sender' && column !== 'subject')) {
      return renderColumn(column);
    }
    if (column !== stackedPrimaryColumn) return null;
    return (
      <span
        key="sender-subject"
        className="mail-primary-stack flex min-w-32 flex-1 flex-col justify-center gap-0.5 overflow-hidden leading-tight"
        style={{ flexBasis: Math.max(widths.sender, widths.subject) }}
      >
        <span className="min-w-0 truncate" title={senderText}>
          <HighlightedSearchText text={senderText} query={searchQuery} />
        </span>
        <span className="min-w-0 truncate" title={subjectText}>
          <HighlightedSearchText text={subjectText} query={searchQuery} />
        </span>
      </span>
    );
  };

  return (
    <div
      data-thread-id={row.id}
      draggable={dragEnabled && !pending}
      onDragStart={(event) => {
        if ((event.target as HTMLElement).closest('button')) {
          event.preventDefault();
          return;
        }
        onDragStart(event);
      }}
      onDragEnd={onDragEnd}
      onContextMenu={(event) => {
        event.preventDefault();
        onContextMenu(event.clientX, event.clientY);
      }}
      className={`group relative flex h-full items-center border-b border-line px-2 hover:z-10 hover:shadow-sm ${
        dragEnabled ? 'cursor-grab active:cursor-grabbing' : ''
      } ${
        density === 'compact' ? 'text-[13px]' : density === 'spacious' ? 'text-[15px]' : 'text-sm'
      } ${
        selected
          ? 'bg-accent-soft'
          : previewed
            ? 'bg-accent-soft/60'
            : row.unread
              ? 'bg-surface'
              : 'bg-app text-ink'
      } ${row.unread ? 'font-semibold' : ''} ${
        active ? 'z-10 outline outline-1 -outline-offset-1 outline-accent' : ''
      }`}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={selected}
        aria-label={selected ? 'Deselect conversation' : 'Select conversation'}
        onClick={(event) => onToggleSelection(event.shiftKey)}
        onFocus={onActivate}
        className={`${actionButton} mr-0.5 shrink-0`}
        title="Select (x); Shift-click for a range"
      >
        {selected ? (
          <CheckSquare2 className="size-4 text-accent" />
        ) : (
          <Square className="size-4 text-ink-muted/50" />
        )}
      </button>
      <button
        type="button"
        onClick={() => onAction(row.starred ? 'unstar' : 'star')}
        onFocus={onActivate}
        disabled={pending}
        className={`${actionButton} mr-1 shrink-0`}
        title={row.starred ? 'Remove star' : 'Star'}
        aria-pressed={row.starred}
      >
        <Star
          className={`size-4 ${row.starred ? 'fill-amber-400 text-amber-400' : 'text-ink-muted/40'}`}
        />
      </button>
      <Link
        {...linkProps}
        onClick={onActivate}
        onFocus={() => {
          onActivate();
          prefetchNow();
        }}
        onPointerEnter={(event) => {
          if (event.pointerType === 'mouse') schedulePrefetch();
        }}
        onPointerLeave={cancelScheduledPrefetch}
        onPointerDown={prefetchNow}
        aria-current={previewed ? 'page' : undefined}
        className="flex h-full min-w-0 flex-1 items-center gap-3 overflow-hidden pr-2"
      >
        {visibleColumns.map(renderVisibleColumn)}
      </Link>
      <div className="pointer-events-none absolute right-2 flex items-center bg-inherit opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100">
        <button
          type="button"
          onClick={() => onAction(row.unread ? 'read' : 'unread')}
          onFocus={onActivate}
          disabled={pending}
          className={actionButton}
          title={row.unread ? 'Mark read' : 'Mark unread'}
        >
          {row.unread ? <MailOpen className="size-4" /> : <Mail className="size-4" />}
        </button>
        {currentSpecialUse !== 'archive' && currentSpecialUse !== 'all' ? (
          <button
            type="button"
            onClick={() => onAction('archive', 'Archived')}
            onFocus={onActivate}
            disabled={pending}
            className={actionButton}
            title="Archive"
          >
            <Archive className="size-4" />
          </button>
        ) : null}
        {currentSpecialUse !== 'trash' ? (
          <button
            type="button"
            onClick={() => onAction('trash', 'Moved to Trash')}
            onFocus={onActivate}
            disabled={pending}
            className={actionButton}
            title="Move to Trash"
          >
            <Trash2 className="size-4" />
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={onMoveToInbox}
              onFocus={onActivate}
              disabled={pending || !onMoveToInbox}
              className={actionButton}
              title={onMoveToInbox ? 'Move to Inbox' : 'Inbox unavailable'}
              aria-label="Move conversation to Inbox"
            >
              <Inbox className="size-4" />
            </button>
            <button
              type="button"
              onClick={() => onAction('delete_perm')}
              onFocus={onActivate}
              disabled={pending}
              className={`${actionButton} text-danger`}
              title="Delete forever"
            >
              <Trash2 className="size-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function AccountIdentity({ account, mode }: { account: Account; mode: AccountIdentityMode }) {
  if (mode === 'color') {
    return (
      <span
        className="h-5 w-[3px] shrink-0 rounded"
        style={{ backgroundColor: account.color }}
        title={`${account.name} · ${account.email}`}
        aria-label={`Account: ${account.name}`}
      />
    );
  }
  return (
    <span
      className="flex h-full w-full items-center gap-1.5 truncate text-xs font-normal text-ink-muted"
      title={`${account.name} · ${account.email}`}
    >
      {mode === 'both' ? (
        <span
          className="size-2 shrink-0 rounded-full"
          style={{ backgroundColor: account.color }}
        />
      ) : null}
      <span className="truncate">{account.name}</span>
    </span>
  );
}

function actionSuccessMessage(
  action: MessageActionRequest['action'],
  count: number,
): string | null {
  const target = count === 1 ? 'conversation' : `${count} conversations`;
  switch (action) {
    case 'read':
      return `Marked ${target} read`;
    case 'unread':
      return `Marked ${target} unread`;
    case 'star':
      return `Starred ${target}`;
    case 'unstar':
      return `Removed star from ${target}`;
    case 'archive':
      return `Archived ${target}`;
    case 'trash':
      return `Moved ${target} to Trash`;
    case 'spam':
      return `Reported ${target} as spam`;
    case 'not_spam':
      return `Marked ${target} as not spam`;
    case 'move':
      return `Moved ${target}`;
    case 'delete_perm':
      return `Deleted ${target} forever`;
  }
}

function emptyTrashError(error: unknown): string {
  if (!(error instanceof Error)) return 'Empty Trash stopped unexpectedly.';
  switch (error.message) {
    case 'trash_mirror_incomplete':
      return 'Wintermute must finish syncing and verifying this Trash folder first. Sync the account, then try again.';
    case 'account_disabled':
      return 'Enable this mail account before emptying its Trash.';
    case 'empty_trash_requires_trash':
      return 'Only a provider Trash folder can be emptied.';
    default:
      return error.message || 'Empty Trash stopped unexpectedly.';
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'The request failed.';
}
